import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '@redux/hooks';
import { setUserDetails } from '@redux/reducers/user.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { toast } from 'react-toastify';
import userService from '@services/user';
import { OrganizationStatus } from '@models/organization.model';
import { useTranslation } from '@hooks/useTranslation';

interface RefreshResult {
  success: boolean;
  organizationStatus?: string;
  shouldRedirect?: boolean;
  redirectPath?: string;
}

/**
 * Hook to refresh user and organization data from the server
 * Useful for checking if organization status has changed (e.g., from SUSPENDED to ENABLED)
 */
export const useRefreshUserOrganization = () => {
  const tActionMessages = useTranslation('actionMessages');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<RefreshResult> => {
      const data = await userService.getSelfWithOrganization();

      if (!data.organization) {
        return {
          success: false,
          shouldRedirect: true,
          redirectPath: '/no-organization',
        };
      }

      // Update Redux state with fresh data
      dispatch(setUserDetails(data.user));
      dispatch(setOrganizationData(data.organization));

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });

      const orgStatus = data.organization.status;

      // Check organization status and determine redirect
      if (orgStatus === OrganizationStatus.ENABLED) {
        return {
          success: true,
          organizationStatus: orgStatus,
          shouldRedirect: true,
          redirectPath: '/',
        };
      }

      if (orgStatus === OrganizationStatus.SUSPENDED) {
        return {
          success: true,
          organizationStatus: orgStatus,
          shouldRedirect: false,
        };
      }

      if (orgStatus === OrganizationStatus.DISABLED) {
        return {
          success: true,
          organizationStatus: orgStatus,
          shouldRedirect: true,
          redirectPath: '/organization-disabled',
        };
      }

      return {
        success: true,
        organizationStatus: orgStatus,
        shouldRedirect: false,
      };
    },
    onSuccess: (result) => {
      if (result.organizationStatus === OrganizationStatus.ENABLED) {
        toast.success(tActionMessages.organizationActiveRedirecting);
      } else if (result.organizationStatus === OrganizationStatus.SUSPENDED) {
        toast.info(tActionMessages.organizationStillSuspended);
      }

      if (result.shouldRedirect && result.redirectPath) {
        history.push(result.redirectPath);
      }
    },
    onError: (error: any) => {
      console.error('Error refreshing organization status:', error);
      toast.error(tActionMessages.failedToCheckOrganizationStatus);
    },
  });
};

export default useRefreshUserOrganization;
